/**
 * Module-level holder for the current Company-issued JWT and a refresh hook.
 *
 * Why a module global instead of React context? `fetchWithAuth` is called
 * from regular TS modules (services, hooks, query functions) that don't have
 * access to React hooks. AuthContext owns the canonical state and pushes
 * changes here; `fetchWithAuth` reads from here.
 *
 * The JWT is intentionally not persisted to localStorage — XSS would
 * exfiltrate it. The parent-domain `company_session` cookie (HttpOnly) is
 * the durable identity; a fresh JWT is minted on every page load via
 * `companyAuth.refreshJwt()`.
 */

let currentJwt: string | null = null;
let refresher: (() => Promise<string | null>) | null = null;
let onClaimUnclaimed: ((companyUserId: string) => void) | null = null;
let onSessionLost: (() => void) | null = null;

export function setJwt(jwt: string | null): void {
  currentJwt = jwt;
}

export function getJwt(): string | null {
  return currentJwt;
}

export function setRefresher(fn: () => Promise<string | null>): void {
  refresher = fn;
}

export async function triggerRefresh(): Promise<string | null> {
  if (!refresher) return null;
  return await refresher();
}

export function setUnclaimedHandler(fn: (companyUserId: string) => void): void {
  onClaimUnclaimed = fn;
}

export function notifyUnclaimed(companyUserId: string): void {
  onClaimUnclaimed?.(companyUserId);
}

export function setSessionLostHandler(fn: () => void): void {
  onSessionLost = fn;
}

export function notifySessionLost(): void {
  onSessionLost?.();
}
